import axiosInstance from "./httpInterceptor";

/**
 * Service for handling administration operations
 */
export const adminService = {

  // ============================
  // PRODUCT REVIEW APIS
  // ============================
  /**
   * Fetch a paginated list of ALL canonical products for a company
   * @param {string} companyId The ID of the company
   * @param {number} page The page number (0-indexed)
   * @param {number} size The number of items per page
   * @param {string} searchTerm Optional search term for product display name
   * @returns {Promise<any>} A Page object of canonical products
   */
  getAllProducts: (companyId, page, size, searchTerm = "") => {
    return axiosInstance.get("/v1/products/review/all", {
      params: {
        companyId,
        page,
        size,
        searchTerm,
      },
    });
  },

  /**
   * Fetch all canonical products that need manual review
   */
  getProductsForReview: (companyId) => {
    return axiosInstance.get("/v1/products/review", {
      params: { companyId },
    });
  },

  /**
   * Fetch the full details of a single product for review, including original mentions
   */
  getProductReviewDetails: (productId) => {
    return axiosInstance.get(`/v1/products/review/${productId}/details`);
  },

  /**
   * Approve a product, setting its status to 'VERIFIED'
   */
  approveProduct: (productId) => {
    return axiosInstance.post(`/v1/products/review/${productId}/approve`);
  },

  /**
   * Delete a canonical product permanently
   * @param {string} productId The ID of the product to delete
   * @returns {Promise<any>}
   */
  deleteProduct: (productId) => {
    return axiosInstance.delete(`/v1/products/review/${productId}`);
  },

  /**
   * Remove a single alias from a product
   * @param {string} productId The ID of the product
   * @param {string} aliasToRemove The sanitized alias to remove
   * @returns {Promise<any>}
   */
  removeAlias: (productId, aliasToRemove) => {
    return axiosInstance.patch(`/v1/products/review/${productId}/aliases`, {
      aliasesToRemove: [aliasToRemove],
    });
  },

  /**
   * Mark a verified product as needing review
   */
  disapproveProduct: (productId) => {
    return axiosInstance.post(`/v1/products/review/${productId}/disapprove`);
  },

  /**
   * Merge a source product into a target product
   * @param {string} sourceProductId The ID of the product to merge (and delete)
   * @param {string} targetProductId The ID of the product to merge into
   * @returns {Promise<any>} The updated target product
   */
  mergeProduct: (sourceProductId, targetProductId) => {
    return axiosInstance.post(`/v1/products/review/${sourceProductId}/merge`, null, {
      params: { targetProductId },
    });
  },

  /**
   * Split an alias from a product into a new canonical product
   * @param {string} sourceProductId The ID of the product to split from
   * @param {string} aliasToSplit The un-sanitized alias string to split
   * @returns {Promise<any>} The newly created canonical product
   */
  splitAlias: (sourceProductId, aliasToSplit) => {
    return axiosInstance.post(`/v1/products/review/${sourceProductId}/split`, { aliasToSplit });
  },

  /**
   * Update the primary display name of a product
   * @param {string} productId The ID of the product to update
   * @param {string} newDisplayName The new display name
   * @returns {Promise<any>} The updated canonical product
   */
  updateProductDetails: (productId, newDisplayName) => {
    return axiosInstance.put(`/v1/products/review/${productId}`, { newDisplayName });
  },

  /**
   * Add or remove aliases from a product
   * @param {string} productId The ID of the product
   * @param {Set<string>} aliasesToAdd Set of aliases to add
   * @param {Set<string>} aliasesToRemove Set of aliases to remove
   * @returns {Promise<any>} The updated canonical product
   */
  modifyAliases: (productId, aliasesToAdd, aliasesToRemove) => {
    return axiosInstance.patch(`/v1/products/review/${productId}/aliases`, { aliasesToAdd, aliasesToRemove });
  },

  // ============================
  // SCRAPER CONTROL APIS
  // ============================
  /**
   * Fetch all available scrapers
   * @returns {Promise<any>} A list of scraper entities
   */
  getAllScrapers: () => {
    return axiosInstance.get("/v1/scraper/all");
  },

  /**
   * Enable or disable a specific scraper
   * @param {string} scraperName The name of the scraper
   * @param {boolean} enable True to enable, false to disable
   * @returns {Promise<any>}
   */
  setScraperState: (scraperName, enable) => {
    return axiosInstance.put(`/v1/scraper/${scraperName}/state`, null, {
      params: { enable },
    });
  },

  // ============================
  // CACHE CONTROL APIS
  // ============================
  /**
   * Clear a specific cache
   * @param {string} cacheName - The name of the cache to clear (e.g., 'quick-catchup')
   * @returns {Promise<any>}
   */
  clearCache: (cacheName) => {
    return axiosInstance.delete(`/v1/admin/cache/clear/${cacheName}`);
  },

  // ============================
  // COMPANY APIS
  // ============================
  /**
   * Fetch a list of all companies in the system
   * @returns {Promise<any>} A list of all companies (id, name)
   */
  getAllCompanies: () => {
    return axiosInstance.get("/v1/companies/all");
  },

  // ============================
  // MONITORING APIS
  // ============================
  /**
   * Fetch aggregated health status for all internal services
   * (bridge-collector, news-scrapers, llm-analyzer)
   */
  getSystemHealth: () => axiosInstance.get("/v1/admin/monitor/health"),

  /**
   * Fetch the date of the last successful scrape per data source
   */
  getLastUpdateDate: () => axiosInstance.get("/v1/admin/cache/last-update-date"),

  /**
   * Trigger an immediate run of all news scrapers
   */
  triggerNewsScraper: () => axiosInstance.post("/v1/news/all/scrapers/run"),

  /**
   * Trigger an immediate run of the LinkedIn posts scraper for all companies
   */
  triggerLinkedInScraper: () => axiosInstance.post("/v1/linkedin/posts/scraper/run"),

  /**
   * Trigger an immediate run of the LinkedIn jobs scraper for all companies
   */
  triggerJobsScraper: () => axiosInstance.post("/v1/jobs/scraper/run"),

  /**
   * Re-run the job title classifier on all stored jobs using the latest keyword lists.
   * Use this after a classifier update to avoid a full re-scrape.
   */
  reclassifyJobs: () => axiosInstance.post("/v1/jobs/reclassify"),

  /**
   * Enable or disable LinkedIn job scraping for a company.
   * Set enabled=false for PorterIQ's own non-medical competitors to exclude them
   * from Expansion Trends data.
   */
  setScrapeEnabled: (companyId, enabled) =>
    axiosInstance.put(`/v1/companies/${companyId}/scrape-enabled`, null, { params: { enabled } }),

  /**
   * Clear all caches for a given data source (LINKEDIN_POSTS or NEWS)
   */
  clearCacheBySource: (source) => axiosInstance.delete(`/v1/admin/cache/clear/source/${source}`),

  /**
   * Trigger an immediate run of the ClinicalTrials.gov scraper for all managed competitors.
   * Returns 202 Accepted immediately — scraping and LLM analysis run asynchronously.
   */
  triggerClinicalTrialsScraper: () => axiosInstance.post("/v1/clinical-trials/scraper/run"),

  /**
   * Trigger Product Showdown FDA device scrape (metadata only, no PDF extraction).
   * Returns 202 Accepted immediately — scraping runs asynchronously.
   */
  triggerProductShowdownScraper: () => axiosInstance.post("/v1/product-showdown/scraper/run"),

  /**
   * Trigger Product Showdown PDF extraction for all registered devices.
   * Downloads FDA SSEDs, 510(k) summaries, and manufacturer IFU/KIF PDFs and extracts text.
   * Invalidates cached comparisons only for devices whose PDF text actually changed.
   * Returns 202 Accepted immediately — extraction runs asynchronously.
   */
  triggerProductShowdownPdfExtraction: () => axiosInstance.post("/v1/product-showdown/extract-pdfs"),

  getTokenUsage: (days = 30) => axiosInstance.get("/v1/admin/token-usage", { params: { days } }),

  triggerTokenSnapshot: () => axiosInstance.post("/v1/admin/token-usage/snapshot"),

  // ============================
  // AUDIT / USER ACTIVITY APIS
  // ============================
  getActivityOverview: () => axiosInstance.get("/v1/audit/overview"),

  getUserActivitySummary: (userId) => axiosInstance.get(`/v1/audit/user/${userId}/summary`),

  getUserSessions: (userId, limit = 5) =>
    axiosInstance.get(`/v1/audit/user/${userId}/sessions`, { params: { limit } }),

  getUserTimeline: (userId) => axiosInstance.get(`/v1/audit/user/${userId}/timeline`),

  // ============================
  // USER MANAGEMENT APIS
  // ============================
  /**
   * Create a new user account (Admin only)
   * @param {Object} userData - The user registration data
   * @returns {Promise<any>}
   */
  createUser: (userData) => {
    return axiosInstance.post("/auth/signup", userData);
  },

  /**
   * Fetch all registered users
   * @returns {Promise<Array>} List of user objects
   */
  getAllUsers: () => {
    return axiosInstance.get("/users");
  },

  /**
   * Toggle a user's active status
   * @param {string} userId - The target user's ID
   * @param {boolean} enabled - The new status (true=active, false=disabled)
   * @returns {Promise<any>}
   */
  updateUserStatus: (userId, enabled) => {
    return axiosInstance.patch(`/users/${userId}/status`, null, {
      params: { enabled }
    });
  },

  /**
   * Permanently delete a user account (Admin only).
   * Use with care — this is irreversible. For routine deactivation use updateUserStatus.
   */
  deleteUser: (userId) => axiosInstance.delete(`/users/${userId}`),

  /**
   * Set per-user page access overrides.
   * Pass null for either list to clear that override and revert to the client's ClientConfig.
   * @param {string} userId
   * @param {string[]|null} enabledPages
   * @param {string[]|null} lockedPages
   */
  updateUserPageOverrides: (userId, enabledPages, lockedPages) =>
    axiosInstance.patch(`/users/${userId}/page-overrides`, {
      enabled_pages: enabledPages,
      locked_pages: lockedPages,
    }),
  // ============================

  /**
   * List all active client configs.
   */
  getClients: () => axiosInstance.get("/v1/admin/clients"),

  /**
   * Get a single client config by ID.
   */
  getClient: (id) => axiosInstance.get(`/v1/admin/clients/${id}`),

  /**
   * Create a new client config.
   * @param {Object} data - { client_company_id, name, industry, enabled_pages }
   */
  createClient: (data) => axiosInstance.post("/v1/admin/clients", data),

  /**
   * Update an existing client config.
   * @param {string} id - Client config ID
   * @param {Object} data - { name?, industry?, enabled_pages?, active? }
   */
  updateClient: (id, data) => axiosInstance.put(`/v1/admin/clients/${id}`, data),

  /**
   * Deactivate (soft-delete) a client config.
   */
  deactivateClient: (id) => axiosInstance.delete(`/v1/admin/clients/${id}`),

  /**
   * Get only client organisations (isClient=true) for user creation form.
   * Returns lightweight { id, name, linkedinUrl } objects — never includes competitors.
   */
  getClientOrgs: () => axiosInstance.get("/v1/companies/client-orgs"),

  // ── KOL Profiles ────────────────────────────────────────────────────────────

  getKolProfiles: (companyId) =>
    axiosInstance.get("/v1/kol/profiles", { params: companyId ? { companyId } : {} }),

  createKolProfile: (data) => axiosInstance.post("/v1/kol/profiles", data),

  deleteKolProfile: (kolId) => axiosInstance.delete(`/v1/kol/profiles/${kolId}`),

  triggerKolScraper:   () => axiosInstance.post("/v1/kol/scraper/run"),
  triggerKolAnalysis:  () => axiosInstance.post("/v1/kol/analysis/run"),
  triggerKolReport:    (companyId) => axiosInstance.post("/v1/kol/report/generate", null, { params: { companyId } }),

  getLatestKolReport:  (companyId) => axiosInstance.get("/v1/kol/report/latest", { params: { companyId } }),

  // Competitor Reports
  regenerateCompetitorReport: (competitorId, companyId) =>
    axiosInstance.post(`/v1/reports/generate/${competitorId}`, null, { params: { companyId } }),
  regenerateMarketReport: (companyId) =>
    axiosInstance.post("/v1/reports/generate/market", null, { params: { companyId } }),
};

